<template>
  <div class="landing" @click.capture="preventNavigation">
    <div v-if="sectionEnabled('header')" data-section-key="header" @click.capture.stop.prevent="sectionClick('header')">
      <Navbar :force-guest="true" />
    </div>
    <p v-if="state === 'loading'" role="status">جارِ تحميل المعاينة...</p>
    <p v-else-if="state === 'error'" role="alert">تعذّر تحميل بيانات الصفحة الرئيسية.</p>
    <HomePageRenderer
      :auth="false"
      :user="null"
      :testimonials="testimonials"
      :testimonials-loading="testimonialsLoading"
      :preview="true"
      :selected-key="selectedKey"
      @section-click="sectionClick"
    />
    <div v-if="sectionEnabled('footer')" data-section-key="footer" @click.capture.stop.prevent="sectionClick('footer')">
      <Footer />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue';
import HomePageRenderer from '@/components/home/HomePageRenderer.vue';
import Navbar from '@/components/layout/navbar.vue';
import Footer from '@/components/layout/footer.vue';
import { useHomepageConfig } from '@/composables/useHomepageConfig';
import { useTestimonials } from '@/composables/useTestimonials';

const { setDraftSections, fetchHomepageConfig, getSection, error } = useHomepageConfig();
const { testimonials, testimonialsLoading, fetchTestimonials } = useTestimonials();
const selectedKey = ref(null);
const state = ref('loading');
let disposed = false;

// Trust only the configured portal and the actual parent window. Local setup
// retains the existing homepage_portal_origin override; never trust a query param.
let portalOrigin = null;
try {
  const url = new URL(localStorage.getItem('homepage_portal_origin') || 'https://portal.tarekmath.com');
  if (['https:', 'http:'].includes(url.protocol) && !url.username && !url.password) portalOrigin = url.origin;
} catch { /* Invalid configuration fails closed. */ }

function notify(type, payload = {}) {
  if (portalOrigin && window.parent !== window) {
    window.parent.postMessage({ source: 'homepage-builder', type, payload }, portalOrigin);
  }
}

function onMessage(event) {
  if (event.source !== window.parent || event.origin !== portalOrigin) return;
  const msg = event.data;
  if (!msg || msg.source !== 'homepage-builder') return;
  if (msg.type === 'preview-connect') {
    notify('preview-' + state.value);
  } else if (msg.type === 'draft-update' && Array.isArray(msg.payload?.sections)) {
    setDraftSections(msg.payload.sections);
  } else if (msg.type === 'select-section') {
    selectedKey.value = typeof msg.payload?.key === 'string' ? msg.payload.key : null;
  }
}

function sectionClick(key) {
  notify('section-click', { key });
}

function sectionEnabled(key) {
  return getSection(key)?.is_active !== false;
}

function preventNavigation(event) {
  // Clicking a homepage link selects its editor without leaving the preview.
  if (event.target.closest('a')) event.preventDefault();
}

onMounted(async () => {
  window.addEventListener('message', onMessage);
  notify('preview-loading');
  await Promise.all([fetchHomepageConfig(), fetchTestimonials()]);
  if (disposed) return;
  state.value = error.value ? 'error' : 'ready';
  notify('preview-' + state.value);
});

onBeforeUnmount(() => {
  disposed = true;
  window.removeEventListener('message', onMessage);
});
</script>

<style scoped>
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
.landing { font-family: 'Cairo', sans-serif; direction: rtl; overflow-x: hidden; }
</style>
